import Button from "components/ui/button/Button";
import { routes } from "lib/routes";
import { irregularPlural, isDefined, pluralise } from "lib/utils";
import { CoreNotice } from "../types";
import { ChartData, ChartType, SupplierBuyerNoticeSummary } from "./types";

export const DEFAULT_ALL_PARTNERS_TOKEN = ["SIGNALS:ALL_PARTNERS"];

export const CARD_ICON_COLORS = {
  partners: "yellow500",
  allSuppliers: "#784EE7", // TODO: use theme color
};

const CHART_COLORS = {
  partners: {
    nonSimilar: "#FFEFC0", // this is a bit of a hack but as they're stacked it will look right
    nonSimilarGradientStart: "#FFEFC0",
    nonSimilarGradientEnd: "rgba(255, 239, 192, 0.1)",
    similar: "#FFC82F",
  },
  allSuppliers: {
    nonSimilar: "#E5DDFF",
    nonSimilarGradientStart: "#E5DDFF",
    nonSimilarGradientEnd: "rgba(229, 221, 255, 0.1)",
    similar: "#784EE7",
  },
};

export function getChartData(
  relationshipSummaries?: SupplierBuyerNoticeSummary[],
  includeEmptyResults?: boolean,
): ChartData[] | null {
  if (!relationshipSummaries) return null;

  const summariesToDisplay = includeEmptyResults
    ? relationshipSummaries
    : relationshipSummaries.filter(
        (summary) => summary.allAwards.totalCount > 0 || summary.similarAwards.totalCount > 0,
      );

  return summariesToDisplay.map((summary) => {
    // Calculate non-similar awards for stacked display
    const similarAwards = summary.similarAwards.totalCount;
    const totalAwards = summary.allAwards.totalCount;
    // For stacking, we need the "other" portion separate from similar
    const nonSimilarAwards = Math.max(0, totalAwards - similarAwards);

    return {
      supplier: {
        guid: summary.supplier.id,
        name: summary.supplier.name,
        logoUrl: summary.supplier.logoUrl,
      },
      // For stacked charts, each segment represents its portion only
      nonSimilarAwards,
      similarAwards,
      // Keep original totals for tooltip data
      totalAwards,
      allAwardsTotalValue: summary.allAwards.totalValue,
      similarAwardsTotalValue: summary.similarAwards.totalValue,
    };
  });
}

export type ChartConfigType = {
  [key: string]: {
    label: string;
    color: string;
    gradientStartColor?: string;
    gradientEndColor?: string;
  };
};

export function getChartConfig(chartType: ChartType): ChartConfigType {
  const colors = CHART_COLORS[chartType];
  return {
    nonSimilarAwards: {
      label: "Total awards",
      color: colors.nonSimilar,
      gradientStartColor: colors.nonSimilarGradientStart,
      gradientEndColor: colors.nonSimilarGradientEnd,
    },
    similarAwards: {
      label: "Awards similar to this opportunity",
      color: colors.similar,
    },
  };
}

type EmptyStateConfig = {
  heading: string;
  description: string;
  action?: React.ReactNode;
};

export const EMPTY_STATE_CONFIG: Record<string, EmptyStateConfig> = {
  allSuppliers: {
    heading: "We couldn't find any suppliers who have worked with this buyer in this area",
    description: "This could indicate that this buyer hasn't procured a service like this before",
  },
  noPartnerSignals: {
    heading: "You haven't set up your partners",
    description:
      "Update the partners in your signal settings to view which of your partners have worked with this buyer",
    action: (
      <Button buttonType="secondary" as="a" to={routes.settings}>
        Go to signal settings
      </Button>
    ),
  },
  noPartnerRelationships: {
    heading: "You don't have any partners who have worked with this buyer",
    description:
      "This may be because you haven't told us about all your partners. Update your signal settings for a more accurate view",
    action: (
      <Button buttonType="secondary" as="a" to={routes.settings}>
        Go to signal settings
      </Button>
    ),
  },
};

export function getPartnersCardTitle(count: number) {
  return `${pluralise(count, "Partner")} who ${irregularPlural(
    count,
    "has",
    "have",
  )} worked with this buyer`;
}

export function getSimilarNoticeSuppliers(
  savedNotices: CoreNotice[],
  similarNotices: CoreNotice[],
) {
  const allNotices = [...similarNotices, ...savedNotices];

  return {
    similarNoticeIds: allNotices.map((notice) => notice.procurementStage.id),
    allSupplierIds: allNotices
      .flatMap((notice) => notice.lots ?? [])
      .flatMap((lot) => lot?.suppliers ?? [])
      .map((supplier) => supplier?.guid)
      .filter(isDefined),
  };
}
